import { SITE_METADATA, type SiteMetadata } from '../config/site-metadata'
import { buildOrganizationJsonLd, buildWebsiteJsonLd, type JsonLd } from './Seo.helpers'

type SeoProps = {
  /** Override the metadata source (defaults to the landing values). */
  metadata?: SiteMetadata
  /** Additional JSON-LD blocks to render (e.g. FAQPage for the landing FAQ section). */
  additionalJsonLd?: JsonLd[]
}

/**
 * Emits the landing page's SEO head tags and JSON-LD.
 *
 * `<title>`, `<meta>`, and `<link>` are hoisted into `<head>` by React 19's
 * native metadata support (no head-manager dependency). JSON-LD is rendered
 * in place as inline scripts — consumed by Googlebot, which executes JS — and
 * built from the shared {@link SITE_METADATA} source so the FAQ-driven
 * `FAQPage` JSON-LD (Stage 2) reuses the same mechanism.
 */
export const Seo = ({ metadata = SITE_METADATA, additionalJsonLd }: SeoProps) => {
  const organizationJsonLd = buildOrganizationJsonLd(metadata)
  const websiteJsonLd = buildWebsiteJsonLd(metadata)

  return (
    <>
      <title>{metadata.landingTitle}</title>
      <meta name="description" content={metadata.landingDescription} />
      <link rel="canonical" href={metadata.siteUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={metadata.siteUrl} />
      <meta property="og:title" content={metadata.landingTitle} />
      <meta property="og:description" content={metadata.landingDescription} />
      <meta property="og:image" content={metadata.ogImage} />

      <meta name="twitter:card" content={metadata.twitterCard} />
      <meta name="twitter:url" content={metadata.siteUrl} />
      <meta name="twitter:title" content={metadata.landingTitle} />
      <meta name="twitter:description" content={metadata.landingDescription} />
      <meta name="twitter:image" content={metadata.ogImage} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(organizationJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: safeJsonLd(websiteJsonLd) }}
      />
      {additionalJsonLd?.map((jsonLd, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: safeJsonLd(jsonLd) }}
        />
      ))}
    </>
  )
}

export default Seo

/**
 * Serialize a JSON-LD object to a string safe for inline `<script>` injection.
 *
 * JSON.stringify alone can emit `</script>`, `<!--`, or `-->` if any string
 * value contains those sequences, allowing an attacker to break out of the
 * script tag. Replacing `<`, `>`, and `&` with their Unicode escape equivalents
 * produces legally identical JSON that browsers parse identically while
 * eliminating the injection surface entirely.
 */
function safeJsonLd(value: JsonLd): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
}
